import * as React from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {
	EntityType,
	PropertyDefinition,
	Theme
} from '../types';
import {Box, CircularProgress, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, SxProps, Typography} from '@mui/material';
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {css} from '@emotion/css';
import {entryPropertyGetReadableValue, entryPropertyGetSecondaryValue, extractPropertyValueUrls, isEntryTimestampPropertyDefinition} from '../common/util';
import EntryMenu, { RowMenuOptionArchive, RowMenuOptionUnarchive, RowMenuOptionDelete, RowMenuProps, RowMenuOptionGoTo } from './EntryMenu';
import CenteredEmptyState from './CenteredEmptyState';
import { RepositoryEntriesRowDto } from '../services/repositoryEntries';
import { Link } from 'react-router-dom';
import { ExternalLinkOverlay } from './ExternalLinkOverlay';
import { useSharedView } from '../hooks/useSharedView';

const REORDER_ACTIVATE_TOUCH_TIMEOUT = 500;
const TITLE_CELL_MIN_WIDTH = '180px';

type EntriesTableRowMenuProps = Omit<RowMenuProps,'onRowReorderClick' | 'options'>

interface Props {
	data: RepositoryEntriesRowDto[];
    propertyDefinitions: PropertyDefinition[];
	rowMenuProps: EntriesTableRowMenuProps;
	disableReordering?: boolean;
	isLoading?: boolean;
	getRowDisabled?: (row: RepositoryEntriesRowDto) => boolean;
	footerContent?: React.ReactNode;
	onEntryFocus?: (id: string) => void;
	onEntryBlur?: () => void;
	onRowMenuGoToClick?: (id: string) => void;
	onRowMenuArchiveClick?: (id: string) => void;
	onRowMenuUnarchiveClick?: (id: string) => void;
	onRowMenuDeleteClick?: (id: string) => void;
	onReorder?: (orderedIds: string[]) => void;
	/** Edit a single property; title column uses double-click for edit (same as before drag handle extraction). */
	onCellEdit?: (row: RepositoryEntriesRowDto, def: PropertyDefinition) => void;
	/** When true, `created-at` / `updated-at` are read-only (server-owned timestamps). */
	disableMetaFieldEdit?: boolean;
	/** When set, user date column headers open a menu offering to view the entries as a timeline by that field. */
	onViewAsTimeline?: (def: PropertyDefinition) => void;
}

interface CellContentProps {
	def: PropertyDefinition;
	value: any;
	disabled?: boolean;
	onCellEditClick?: () => void;
	sx?: SxProps<Theme>;
}

const throttle = (func: (...args: any) => any, wait: number) => {
	let lastTime = 0;
	return (...args: any) => {
		const now = new Date().getTime();
		if (now - lastTime >= wait) {
			lastTime = now;
			func(...args);
		}
	};
};

const CellContent = ({def, value, disabled, onCellEditClick, sx}: CellContentProps) => {
	const classes = {
		maxHeight2Lines: css`
			overflow: hidden;
			display: -webkit-box;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
		`,
	};
	const color = disabled ? 'text.secondary' : undefined;
	const secondaryValue = entryPropertyGetSecondaryValue(def, value);
	const editable = Boolean(onCellEditClick) && !disabled;
	const cellHoverSx = editable ? {'&:hover': {bgcolor: 'action.hover'}} : undefined;
	const urls = useMemo(() => Array.from(new Set(extractPropertyValueUrls(def, value))), [def, value]);
	return <TableCell sx={{position: 'relative', cursor: editable ? 'pointer' : undefined, ...cellHoverSx}} onClick={editable ? (e) => {
		e.stopPropagation();
		onCellEditClick?.();
	} : undefined}>
		<Box
			display='flex'
			flexDirection='column'
			sx={{
				userSelect: 'text',
				color,
				...sx
			}}
		>
			<Typography className={classes.maxHeight2Lines}>
				{entryPropertyGetReadableValue(def, value)}
			</Typography>
			{secondaryValue && <Typography variant='caption' color={disabled ? 'text.disabled' : 'primary.light'}>
				{secondaryValue}
			</Typography>}
		</Box>
		{urls.length > 0 && <ExternalLinkOverlay urls={urls} />}
	</TableCell>;
};

function EntriesTable({data, propertyDefinitions, rowMenuProps, disableReordering, getRowDisabled, footerContent, onEntryFocus, onEntryBlur, onRowMenuArchiveClick, onRowMenuUnarchiveClick, onRowMenuDeleteClick, onCellEdit, onReorder, isLoading, disableMetaFieldEdit = false, onViewAsTimeline}: Props) {
	const [dragItem, setDragItem] = useState<any>(null);
	const [touchItem, setTouchItem] = useState<any>(null);
	/** While set, row order follows these ids (see RepositorySettingsView). RTK-backed `data` does not update during drag. */
	const [localOrder, setLocalOrder] = useState<string[] | null>(null);
	const hasWindowScrolledRef = useRef<boolean>(false);
	const {toPath} = useSharedView();
	const rowHoldTimeout = useRef<NodeJS.Timeout | null>(null);

	const classes = {
		hideScrollbar: css`
			-ms-overflow-style: none;  /* Internet Explorer 10+ */
			scrollbar-width: none;  /* Firefox, Safari 18.2+, Chromium 121+ */
			&::-webkit-scrollbar { 
				display: none;  /* Older Safari and Chromium */
			}
		`,
	};

	const entryById = useMemo(() => new Map(data.map((e) => [e.id, e])), [data]);

	const displayData = useMemo((): RepositoryEntriesRowDto[] => {
		if (!localOrder) {
			return data;
		}
		return localOrder.map((id) => entryById.get(id)).filter((x): x is RepositoryEntriesRowDto => x != null);
	}, [data, localOrder, entryById]);

	useEffect(() => {
		if (!localOrder?.length) {
			return;
		}
		const dataIds = data.map((r) => r.id);
		if (dataIds.length !== localOrder.length) {
			return;
		}
		if (dataIds.every((id, i) => id === localOrder[i])) {
			setLocalOrder(null);
		}
	}, [data, localOrder]);

	/** MOUSE EVENT:
	 * while reordering is in progress, publish rank updates by releasing mouse (touch uses per-step commits).
	 */
	const handleMouseUp = useCallback(() => {
		if (!dragItem) {
			return;
		}
		if (localOrder) {
			onReorder?.(localOrder);
		}
		setDragItem(null);
	}, [dragItem, localOrder, onReorder]);

	const handleScroll = useCallback(() => {
		hasWindowScrolledRef.current = true;
	}, []);
	const throttledScrollHandler = throttle(handleScroll, 100);

	useEffect(() => {
		window.addEventListener('scroll', throttledScrollHandler);
		return () => {
			window.removeEventListener('scroll', throttledScrollHandler);
		};
	}, [throttledScrollHandler]);

	useEffect(() => {
		window.addEventListener('mouseup', handleMouseUp);
		return () => {
			window.removeEventListener('mouseup', handleMouseUp);
		};
	}, [handleMouseUp]);

	const reorderRowsDuringMouseDrag = useCallback(
		(targetItem: RepositoryEntriesRowDto) => {
			if (!dragItem) {
				return;
			}
			const orderedIds = localOrder ?? data.map((r) => r.id);
			const fromIndex = orderedIds.indexOf(dragItem.id);
			const toIndex = orderedIds.indexOf(targetItem.id);
			if (fromIndex === -1 || toIndex === -1) {
				return;
			}
			const next = [...orderedIds];
			next.splice(fromIndex, 1);
			next.splice(toIndex, 0, dragItem.id);
			setLocalOrder(next);
		},
		[dragItem, localOrder, data],
	);

	const applyTouchReorder = useCallback(
		(targetItem: RepositoryEntriesRowDto) => {
			const reorderItem = touchItem;
			if (!reorderItem) {
				return;
			}
			const orderedIds = localOrder ?? data.map((r) => r.id);
			const fromIndex = orderedIds.indexOf(reorderItem.id);
			const toIndex = orderedIds.indexOf(targetItem.id);
			if (fromIndex === -1 || toIndex === -1) {
				return;
			}
			const next = [...orderedIds];
			next.splice(fromIndex, 1);
			next.splice(toIndex, 0, reorderItem.id);
			setLocalOrder(next);
			onReorder?.(next);
		},
		[touchItem, localOrder, data, onReorder],
	);

	const reorder = useCallback(
		(reorderItem: RepositoryEntriesRowDto, targetItem: RepositoryEntriesRowDto) => {
			const orderedRows = localOrder
				? localOrder.map((id) => entryById.get(id)).filter((x): x is RepositoryEntriesRowDto => x != null)
				: [...data];
			const newItems = [...orderedRows];
			const draggingIndex = newItems.findIndex((item) => item.id === reorderItem.id);
			const targetIndex = newItems.findIndex((item) => item.id === targetItem.id);
			if (draggingIndex === -1 || targetIndex === -1) {
				return;
			}
			newItems.splice(draggingIndex, 1);
			newItems.splice(targetIndex, 0, reorderItem);
			const newIds = newItems.map((r) => r.id);
			setLocalOrder(newIds);
			onReorder?.(newIds);
		},
		[localOrder, data, entryById, onReorder],
	);

	const handleDragCellTouchHold = (item: any) => {
		setTouchItem(item);
		setLocalOrder((prev) => (prev ? [...prev] : data.map((r) => r.id)));
	};

	const handleRowMenuReorderClick = (item: any) => {
		handleDragCellTouchHold(item);
		rowMenuProps.onClose();
	};

	const handleRowMoveToTopClick = (item: any) => {
		const firstRowItem = displayData[0];
		if (firstRowItem) {
			reorder(item, firstRowItem);
		}
		rowMenuProps.onClose();
	};

	const handleReorderRowTouchHold = () => {
		setTouchItem(null);
	};

	/** TOUCH EVENT:
	 * while reordering is not in progress, set a row into reordering state by holding down touch on its drag handle for [timeout]
	 * unless the page is scrolled within the [timeout]
	 */
	const handleDragCellTouchStart = (e: any, item: any) => {
		if (touchItem) {
			return;
		}
		e.stopPropagation();
		hasWindowScrolledRef.current = false;
		rowHoldTimeout.current = setTimeout(() => {
			if (!hasWindowScrolledRef.current) {
				handleDragCellTouchHold(item);
			}
		}, REORDER_ACTIVATE_TOUCH_TIMEOUT);
	};
	const handleDragCellTouchEnd = () => {
		if (rowHoldTimeout.current) {
			clearTimeout(rowHoldTimeout.current);
		}
	};

	/** TOUCH EVENT:
	 * while reordering is in progress, cancel reordering by holding down touch on the same row after [timeout]
	 * unless touch is ended within [timeout]
	 * or the page is scrolled within [timeout]
	 */
	const handleRowTouchStart = (e: React.TouchEvent<HTMLDivElement>, item: any) => {
		if (!touchItem) {
			return;
		}
		e.stopPropagation();
		hasWindowScrolledRef.current = false;
		rowHoldTimeout.current = setTimeout(() => {
			if (!hasWindowScrolledRef.current) {
				if (touchItem?.id === item.id) {
					handleReorderRowTouchHold();
				}
			}
		}, REORDER_ACTIVATE_TOUCH_TIMEOUT);
	};

	/** TOUCH EVENT:
	 * while reordering is in progress, assign a new rank to the active row by touching another row
	 * unless the page has been scrolled since touch start
	 */
	const handleRowTouchEnd = (e: React.TouchEvent<HTMLDivElement>, item: any) => {
		e.stopPropagation();
		if (rowHoldTimeout.current) {
			clearTimeout(rowHoldTimeout.current);
		}
		if (touchItem && item.id !== touchItem.id && !hasWindowScrolledRef.current) {
			applyTouchReorder(item);
		}
	};

	/** MOUSE EVENT:
	 * set a row into reordering state by pressing mouse on the row drag handle
	 */
	const handleDragCellMouseDown = (e: any, item: any) => {
		e.stopPropagation();
		if (touchItem) {
			return;
		}
		setLocalOrder((prev) => (prev ? [...prev] : data.map((r) => r.id)));
		setDragItem(item);
	};

	/** MOUSE EVENT:
	 * while reordering is in progress, assign a new rank to the active row by moving the cursor over another row
	 */
	const handleRowMouseEnter = (_e: any, targetItem: RepositoryEntriesRowDto) => {
		if (dragItem) {
			reorderRowsDuringMouseDrag(targetItem);
		}
	};

	const reorderItem = dragItem ?? touchItem;

	const [headerMenu, setHeaderMenu] = useState<{ anchorEl: HTMLElement; def: PropertyDefinition } | null>(null);
	const renderHeaderCell = (def: PropertyDefinition, sx: SxProps<Theme>) => {
		const isTimelineColumn = Boolean(onViewAsTimeline) && def.type === EntityType.PropertyDefinitionDate && !isEntryTimestampPropertyDefinition(def);
		return (
			<TableCell
				key={def.id}
				sx={{color: 'text.secondary', ...(isTimelineColumn ? {cursor: 'pointer', '&:hover': {color: 'text.primary'}} : {}), ...sx}}
				onClick={isTimelineColumn ? (e) => setHeaderMenu({anchorEl: e.currentTarget, def}) : undefined}
			>
				{def.label}
			</TableCell>
		);
	};

	const hasColumnSchema = propertyDefinitions.length > 0;

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: '100%'}}>
			<TableContainer className={classes.hideScrollbar} sx={{ flex: 1, minHeight: 0, overflow: 'auto', borderRadius: 6, position: 'relative' }}>
				{!hasColumnSchema ? (
					isLoading ? (
						<Box sx={{display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center', minHeight: 200}}>
							<CircularProgress />
						</Box>
					) : (
						<CenteredEmptyState message="This repository has no fields to display in the table." />
					)
				) : !data.length && !isLoading ? (
					<CenteredEmptyState />
				) : (
					<Table aria-label="simple table" size="small" stickyHeader sx={{pb: 18}}>
						<TableHead>
							<TableRow>
								<TableCell align='right'padding='checkbox' />
								{renderHeaderCell(propertyDefinitions[0], {minWidth: TITLE_CELL_MIN_WIDTH})}
								{propertyDefinitions.slice(1).map((propertyDefinition) => renderHeaderCell(propertyDefinition, {}))}
								<TableCell align='right' />
							</TableRow>
						</TableHead>
						<TableBody>
							{displayData.map((row) => {
								const rowDisabled = getRowDisabled?.(row) ?? false;
								const rowDisableDrag = rowDisabled || Boolean(disableReordering);
								const titleDef = propertyDefinitions[0];
								const titleCellDisabled =
								rowDisabled ||
								(disableMetaFieldEdit && isEntryTimestampPropertyDefinition(titleDef));
								return <TableRow
									key={row.id}
									sx={{ '&:last-child td, &:last-child th': { border: 0 }, userSelect: 'none'}}
									onMouseEnter={(e) => handleRowMouseEnter(e, row)}
									onTouchEnd={(e) => handleRowTouchEnd(e, row)}
									onTouchStart={(e) => handleRowTouchStart(e, row)}
									selected={row.id === reorderItem?.id}
									style={{
										height: 33,
									}}
								>
									<TableCell align="right" sx={{whiteSpace: 'nowrap'}} padding='none'>
										{!rowDisableDrag && (
											<IconButton
											// size="small"
												aria-label="Reorder row"
												onMouseDown={(e) => handleDragCellMouseDown(e, row)}
												onTouchStart={(e) => handleDragCellTouchStart(e, row)}
												onTouchEnd={() => handleDragCellTouchEnd()}
												tabIndex={-1}
												sx={{
													color: 'text.secondary',
													opacity: 0.4,
													cursor: reorderItem?.id === row.id ? 'grabbing' : 'grab',
													'&:hover': {
														opacity: 1,
														backgroundColor: 'transparent',
													},
												}}
											>
												<DragIndicatorIcon />
											</IconButton>
										)}
									</TableCell>
									<CellContent
										def={titleDef}
										value={row.data[titleDef.slug]}
										disabled={titleCellDisabled}
										onCellEditClick={
											onCellEdit && !titleCellDisabled
												? () => onCellEdit(row, titleDef)
												: undefined
										}
										sx={{minWidth: TITLE_CELL_MIN_WIDTH}}
									/>
									{propertyDefinitions.slice(1).map((def) => {
										const cellDisabled =
										rowDisabled ||
										(disableMetaFieldEdit && isEntryTimestampPropertyDefinition(def));
										return (
											<CellContent
												key={def.slug}
												def={def}
												value={row.data[def.slug]}
												disabled={cellDisabled}
												onCellEditClick={
													onCellEdit && !cellDisabled
														? () => onCellEdit(row, def)
														: undefined
												}
											/>
										);
									})}
									{rowMenuProps.onMenuBtnClick && <TableCell align="right" sx={{whiteSpace: 'nowrap'}} padding='none'>
										<Box sx={{display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25}}>
											<IconButton onClick={(e) => rowMenuProps.onMenuBtnClick?.(row, e.target as Element)} onFocus={onEntryFocus && (() => onEntryFocus(row.id))} onBlur={onEntryBlur && (() => onEntryBlur())}>
												<MoreVertIcon sx={{color: 'text.secondary'}} />
											</IconButton>
										</Box>
									</TableCell>}
								</TableRow>;
							})}
						</TableBody>
					</Table>
				)}
				{footerContent}
			</TableContainer>
			<EntryMenu
				anchorEl={rowMenuProps.anchorEl}
				onClose={rowMenuProps.onClose}
				item={rowMenuProps.item}
				options={[]}
				onRowReorderClick={handleRowMenuReorderClick}
				onRowMoveToTopClick={handleRowMoveToTopClick}
			>
				<Link to={toPath(`/entries/${rowMenuProps.item?.id ?? ''}`)} style={{textDecoration: 'none'}}><MenuItem>
					<RowMenuOptionGoTo />
				</MenuItem></Link>
				{!rowMenuProps.item?.isArchived
					? onRowMenuArchiveClick && <MenuItem onClick={() => onRowMenuArchiveClick(rowMenuProps.item?.id ?? '')} >
						<RowMenuOptionArchive />
					</MenuItem>
					: onRowMenuUnarchiveClick && <MenuItem onClick={() => onRowMenuUnarchiveClick(rowMenuProps.item?.id ?? '')} >
						<RowMenuOptionUnarchive />
					</MenuItem>}
				{onRowMenuDeleteClick && <MenuItem onClick={() => onRowMenuDeleteClick(rowMenuProps.item?.id ?? '')} >
					<RowMenuOptionDelete />
				</MenuItem>}
			</EntryMenu>
			<Menu anchorEl={headerMenu?.anchorEl} open={Boolean(headerMenu)} onClose={() => setHeaderMenu(null)}>
				<MenuItem
					onClick={() => {
						if (headerMenu) onViewAsTimeline?.(headerMenu.def);
						setHeaderMenu(null);
					}}
				>
					<ListItemIcon><ViewTimelineIcon fontSize="small" /></ListItemIcon>
					<ListItemText>View as timeline</ListItemText>
				</MenuItem>
			</Menu>
		</Box>
	);
}

export default EntriesTable;
